/**
 * Adds columns to the DBF (attribute table) of an existing shapefile, keeping all original columns and values byte for
 * byte. The .shp/.shx stay untouched, so record n in the DBF still belongs to geometry n.
 * Columns with the same name as an added column (e.g. from an earlier export) are replaced.
 */

export interface DbfField {
    /** At most 10 characters */
    name: string;
    /** C = text, N = number, D = date */
    type: 'C' | 'N' | 'D';
    length: number;
    decimals?: number;
}

export type DbfValue = string | number | Date | null | undefined;

interface ExistingField {
    descriptor: Uint8Array;
    name: string;
    offset: number;
    length: number;
}

const HEADER_LENGTH = 32;
const DESCRIPTOR_LENGTH = 32;
const HEADER_TERMINATOR = 0x0D;
const END_OF_FILE = 0x1A;
// Visual FoxPro tables store the offset of a field within the record in the descriptor
const VISUAL_FOXPRO_VERSIONS = [0x30, 0x31, 0x32];

/**
 * @param dbf the original .dbf
 * @param fields the columns to add
 * @param valuesForRecord the values for the added columns of record n (0-based), in the order of `fields`
 * @param encoding the contents of the .cpg, UTF-8 when missing or unknown
 */
export function addDbfFields(dbf: ArrayBuffer | Uint8Array,
                             fields: DbfField[],
                             valuesForRecord: (record: number) => DbfValue[],
                             encoding?: string): Uint8Array {
    const source = dbf instanceof Uint8Array ? dbf : new Uint8Array(dbf);
    const view = new DataView(source.buffer, source.byteOffset, source.byteLength);
    const version = source[0];
    const recordCount = view.getUint32(4, true);
    const headerLength = view.getUint16(8, true);
    const recordLength = view.getUint16(10, true);

    // Read the existing field descriptors, the first byte of each record is the deleted flag
    const existingFields: ExistingField[] = [];
    let terminatorPosition = HEADER_LENGTH;
    for (let offset = 1; terminatorPosition < headerLength && source[terminatorPosition] !== HEADER_TERMINATOR; terminatorPosition += DESCRIPTOR_LENGTH) {
        const descriptor = source.slice(terminatorPosition, terminatorPosition + DESCRIPTOR_LENGTH);
        const length = descriptor[16];
        existingFields.push({descriptor, name: readFieldName(descriptor), offset, length});
        offset += length;
    }

    const addedNames = fields.map(field => field.name.toUpperCase());
    const keptFields = existingFields.filter(field => !addedNames.includes(field.name.toUpperCase()));
    // Bytes after the header terminator (e.g. the Visual FoxPro backlink) are kept as they are
    const headerTail = source.slice(terminatorPosition + 1, headerLength);

    const newRecordLength = 1 + keptFields.reduce((sum, field) => sum + field.length, 0) + fields.reduce((sum, field) => sum + field.length, 0);
    const newHeaderLength = HEADER_LENGTH + DESCRIPTOR_LENGTH * (keptFields.length + fields.length) + 1 + headerTail.length;
    if (newRecordLength > 0xFFFF) {
        throw new Error('De records van het shapefile worden te groot om kolommen toe te voegen');
    }

    const result = new Uint8Array(newHeaderLength + newRecordLength * recordCount + 1);
    const resultView = new DataView(result.buffer);

    // Header: same version and language driver, new date and lengths
    result.set(source.slice(0, HEADER_LENGTH), 0);
    const today = new Date();
    result[1] = today.getFullYear() - 1900;
    result[2] = today.getMonth() + 1;
    result[3] = today.getDate();
    resultView.setUint32(4, recordCount, true);
    resultView.setUint16(8, newHeaderLength, true);
    resultView.setUint16(10, newRecordLength, true);

    // Field descriptors
    const isVisualFoxPro = VISUAL_FOXPRO_VERSIONS.includes(version);
    let position = HEADER_LENGTH;
    let fieldOffset = 1;
    keptFields.forEach(field => {
        result.set(field.descriptor, position);
        if (isVisualFoxPro) {
            resultView.setUint32(position + 12, fieldOffset, true);
        }
        position += DESCRIPTOR_LENGTH;
        fieldOffset += field.length;
    });
    fields.forEach(field => {
        result.set(createDescriptor(field, isVisualFoxPro ? fieldOffset : 0), position);
        position += DESCRIPTOR_LENGTH;
        fieldOffset += field.length;
    });
    result[position] = HEADER_TERMINATOR;
    result.set(headerTail, position + 1);

    // Records: deleted flag and kept values are copied, the added values are written behind them
    const encode = createEncoder(encoding);
    for (let record = 0; record < recordCount; record++) {
        const sourceStart = headerLength + record * recordLength;
        let target = newHeaderLength + record * newRecordLength;
        result[target++] = source[sourceStart];
        keptFields.forEach(field => {
            result.set(source.subarray(sourceStart + field.offset, sourceStart + field.offset + field.length), target);
            target += field.length;
        });
        const values = valuesForRecord(record);
        fields.forEach((field, index) => {
            result.set(formatValue(field, values[index], encode), target);
            target += field.length;
        });
    }
    result[result.length - 1] = END_OF_FILE;

    return result;
}

/** The values of one column as trimmed text, '' when empty or when the column does not exist */
export function readDbfColumn(dbf: ArrayBuffer | Uint8Array, name: string, encoding?: string): string[] {
    const source = dbf instanceof Uint8Array ? dbf : new Uint8Array(dbf);
    const view = new DataView(source.buffer, source.byteOffset, source.byteLength);
    const recordCount = view.getUint32(4, true);
    const headerLength = view.getUint16(8, true);
    const recordLength = view.getUint16(10, true);

    let field: { offset: number, length: number } = null;
    for (let position = HEADER_LENGTH, offset = 1; position < headerLength && source[position] !== HEADER_TERMINATOR; position += DESCRIPTOR_LENGTH) {
        const descriptor = source.subarray(position, position + DESCRIPTOR_LENGTH);
        if (readFieldName(descriptor).toUpperCase() === name.toUpperCase()) {
            field = {offset, length: descriptor[16]};
        }
        offset += descriptor[16];
    }

    const decode = createDecoder(encoding);
    const values: string[] = [];
    for (let record = 0; record < recordCount; record++) {
        const start = headerLength + record * recordLength;
        values.push(field ? decode(source.subarray(start + field.offset, start + field.offset + field.length)).trim() : '');
    }
    return values;
}

function readFieldName(descriptor: Uint8Array): string {
    let name = '';
    for (let i = 0; i < 11 && descriptor[i] !== 0; i++) {
        name += String.fromCharCode(descriptor[i]);
    }
    return name.trim();
}

function createDescriptor(field: DbfField, displacement: number): Uint8Array {
    const descriptor = new Uint8Array(DESCRIPTOR_LENGTH);
    const name = field.name.toUpperCase().slice(0, 10);
    for (let i = 0; i < name.length; i++) {
        descriptor[i] = name.charCodeAt(i);
    }
    descriptor[11] = field.type.charCodeAt(0);
    new DataView(descriptor.buffer).setUint32(12, displacement, true);
    descriptor[16] = field.length;
    descriptor[17] = field.decimals ?? 0;
    return descriptor;
}

function formatValue(field: DbfField, value: DbfValue, encode: (text: string) => Uint8Array): Uint8Array {
    const bytes = new Uint8Array(field.length).fill(0x20); // empty value = spaces
    if (value === null || value === undefined || value === '') {
        return bytes;
    }
    if (field.type === 'D') {
        const date = value instanceof Date ? value : new Date(value);
        if (!isNaN(date.getTime())) {
            const text = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
            bytes.set(encode(text).subarray(0, field.length));
        }
        return bytes;
    }
    if (field.type === 'N') {
        const number = typeof value === 'number' ? value : parseFloat(String(value));
        if (Number.isFinite(number)) {
            const text = number.toFixed(field.decimals ?? 0);
            if (text.length <= field.length) {
                bytes.set(encode(text), field.length - text.length); // numbers are right aligned
            }
        }
        return bytes;
    }
    // Text: on one line, cut off at the field length without breaking a multibyte character
    // Every character takes at least one byte, so first cut off at the field length in characters
    let text = String(value).replace(/\s+/g, ' ').trim().slice(0, field.length);
    let encoded = encode(text);
    while (encoded.length > field.length) {
        text = text.slice(0, -1);
        encoded = encode(text);
    }
    bytes.set(encoded);
    return bytes;
}

function createEncoder(encoding?: string): (text: string) => Uint8Array {
    const normalized = (encoding ?? '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (['', 'UTF8'].includes(normalized)) {
        const encoder = new TextEncoder();
        return text => encoder.encode(text);
    }
    // Other code pages (e.g. 1252 / ISO-8859-1): one byte per character
    return text => Uint8Array.from(text, char => char.charCodeAt(0) < 256 ? char.charCodeAt(0) : 0x3F);
}

function createDecoder(encoding?: string): (bytes: Uint8Array) => string {
    const normalized = (encoding ?? '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    const decoder = new TextDecoder(['', 'UTF8'].includes(normalized) ? 'utf-8' : 'windows-1252');
    return bytes => decoder.decode(bytes).replace(/\0/g, '');
}

function pad(value: number): string {
    return value < 10 ? '0' + value : String(value);
}
